import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import "./AdminAnnouncements.css";
const toArray = (x) =>
  Array.isArray(x) ? x : (x?.data && Array.isArray(x.data) ? x.data : []);

const makeExcerpt = (text = "", max = 140) => {
  const t = String(text || "").trim();
  if (!t) return "";
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  const base = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  return base.concat("…");
};

export default function AdminAnnouncements() {
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dept, setDept] = useState("all");
  const [empName, setEmpName] = useState("all");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [histError, setHistError] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [histDept, setHistDept] = useState("all");
  const [histEmp, setHistEmp] = useState("all");
  const [histQuery, setHistQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch("https://hrms.urbango.co.in/server/index.php?endpoint=departments")
        .then((r) => r.json()).catch(() => []),
      fetch("https://hrms.urbango.co.in/server/index.php?endpoint=employees")
        .then((r) => r.json()).catch(() => []),
    ]).then(([depRes, empRes]) => {
      const deps = toArray(depRes);
      const emps = toArray(empRes);
      const depSet = new Set(deps.map((d) => d.name).filter(Boolean));
      if (depSet.size === 0) {
        emps.forEach((e) => { if (e.department) depSet.add(e.department); });
      }
      setDepartments(["all", ...Array.from(depSet)]);
      setEmployees(emps);
    });
    refreshHistory();
  }, []);

  const refreshHistory = () => {
    setLoadingHistory(true); setHistError("");
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=announce_list")
      .then((r) => r.json())
      .then((data) => {
        const rows = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        const sorted = rows.sort((a, b) => new Date(b.Date || b.date || 0) - new Date(a.Date || a.date || 0));
        setHistory(sorted);
        setPage(1);
      })
      .catch(() => setHistError("Unable to load announcement history."))
      .finally(() => setLoadingHistory(false));
  };

  const employeeOptions = useMemo(() => {
    const src = dept === "all"
      ? employees
      : employees.filter((e) => (e.department || "").toLowerCase() === String(dept).toLowerCase());
    const allLabel = dept === "all" ? "All Employees (company)" : "All Employees (dept)";
    return [{ employeeName: "all", label: allLabel }, ...src.map((e) => ({
      employeeName: e.employeeName || e.name || "",
      label: e.employeeName || e.name || "Unnamed",
    }))];
  }, [dept, employees]);
  useEffect(() => { setEmpName("all"); }, [dept]);

  const histEmployeeOptions = useMemo(() => {
    const src = histDept === "all"
      ? employees
      : employees.filter((e) => (e.department || "").toLowerCase() === String(histDept).toLowerCase());
    const allLabel = histDept === "all" ? "All Employees (company)" : "All Employees (dept)";
    return [{ employeeName: "all", label: allLabel }, ...src.map((e) => ({
      employeeName: e.employeeName || e.name || "",
      label: e.employeeName || e.name || "Unnamed",
    }))];
  }, [histDept, employees]);
  useEffect(() => { setHistEmp("all"); }, [histDept]);

  const normalizeDept = (d) => {
    const x = String(d || "").toLowerCase();
    return x === "all departments" || x === "all department" || x === "all" ? "all" : d;
  };
  const normalizeEmp = (n) => {
    const x = String(n || "").toLowerCase();
    return x === "all employees" || x === "all employee" || x === "all" ? "all" : n;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      setStatus("Title and message are required.");
      return;
    }
    setSaving(true); setStatus("");
    const payload = {
      date,
      department: normalizeDept(dept),
      employeeName: normalizeEmp(empName),
      title,
      messages: message,
    };
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=add_announcement", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res?.success) {
          setStatus("Announcement sent."); setTitle(""); setMessage(""); setEmpName("all");
          refreshHistory();
        } else {
          setStatus(res?.error || "Failed to send.");
        }
      })
      .catch(() => setStatus("Network error while sending."))
      .finally(() => setSaving(false));
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete "${row.Title}" dated ${row.Date}?`)) return;
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=delete_announcement", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: row.Title, date: row.Date }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res?.success) {
          setModalOpen(false);
          refreshHistory();
        } else {
          alert(res?.error || "Delete failed.");
        }
      })
      .catch(() => alert("Network error while deleting."));
  };

  const filteredHistory = useMemo(() => {
    const d = String(histDept || "").toLowerCase();
    const e = String(histEmp || "").toLowerCase();
    const q = String(histQuery || "").toLowerCase();
    return history.filter((row) => {
      const rd = String(row.Department || "").toLowerCase();
      const re = String(row.EmployeeName || "").toLowerCase();
      const t = String(row.Title || "").toLowerCase();
      const m = String(row.Messages || "").toLowerCase();
      const passDept = d === "all" || rd === "all" || rd === d;
      const passEmp = e === "all" ? true : re === e;
      const passQuery = !q || t.includes(q) || m.includes(q);
      return passDept && passEmp && passQuery;
    });
  }, [history, histDept, histEmp, histQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const paged = filteredHistory.slice(start, start + PAGE_SIZE);
  useEffect(() => { setPage(1); }, [histDept, histEmp, histQuery]);

  const openModal = (row) => { setActiveRow(row); setModalOpen(true); };

  return (
    <>
      <div className="announce-wrap container-fluid">
        <div className="announce-card glass col-12 col-md-8">
          <div className="announce-header">
            <h2 className="announcement-title">{'Create Notification'}</h2>
            <p>Share an upcoming event, policy note, or general update.</p>
          </div>
          <form onSubmit={handleSubmit} className="announce-form">
            <div className="row">
              <div className="field col-12 col-sm-4">
                <label>Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="field col-12 col-sm-4">
                <label>Department</label>
                <select value={dept} onChange={(e) => setDept(e.target.value)}>
                  {departments.map((d) => (
                    <option key={d} value={d}>{d === "all" ? "All Departments" : d}</option>
                  ))}
                </select>
              </div>
              <div className="field col-12 col-sm-4">
                <label>Employee</label>
                <select value={empName} onChange={(e) => setEmpName(e.target.value)}>
                  {employeeOptions.map((e, i) => {
                    const label = e.label ?? (e.employeeName === "all" ? "All Employees" : e.employeeName || "Unnamed");
                    const val = e.employeeName || "all";
                    return <option key={`${val}-${i}`} value={val}>{label}</option>;
                  })}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Title</label>
              <input type="text" placeholder="e.g., Diwali Celebration"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
              />
            </div>
            <div className="field">
              <label>Message</label>
              <textarea
                rows={5}
                placeholder="Write details for the event/announcement..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <div className="actions">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? "Sending..." : "Publish"}
              </button>
              {status && <span className="status-text">{status}</span>}
            </div>
          </form>
        </div>
        <div className="announce-right col-12 col-md-4">
          <div className="announce-tips glass">
            <h4>Tips</h4>
            <ul>
              <li>Department = All + Employee = All Employees broadcasts to everyone.</li>
              <li>Department only + Employee = All broadcasts to the department only.</li>
              <li>Keep titles concise and descriptive.</li>
            </ul>
          </div>
          <div className="announce-history glass">
            <div className="hist-head">
              <div className="hist-filters">
                <select value={histDept} onChange={(e) => setHistDept(e.target.value)}>
                  {departments.map((d) => (
                    <option key={`h-${d}`} value={d}>{d === "all" ? "All Departments" : d}</option>
                  ))}
                </select>
                <select value={histEmp} onChange={(e) => setHistEmp(e.target.value)}>
                  {histEmployeeOptions.map((e, i) => {
                    const label = e.label ?? (e.employeeName === "all" ? "All Employees" : e.employeeName || "Unnamed");
                    const val = e.employeeName || "all";
                    return <option key={`he-${val}-${i}`} value={val}>{label}</option>;
                  })}
                </select>
                <input className="hist-search"
                  placeholder="Search title/message..."
                  value={histQuery}
                  onChange={(e) => setHistQuery(e.target.value)}
                />
              </div>
              <div className="hist-controls">
                <button className="btn-lite" disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
                <span className="hist-page">{page}/{totalPages}</span>
                <button className="btn-lite" disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
                <button className="btn-lite" onClick={refreshHistory}>Refresh</button>
              </div>
            </div>
            <div className="tabs-body">
              {loadingHistory ? (
                <div className="hist-empty">Loading…</div>
              ) : histError ? (
                <div className="hist-empty">{histError}</div>
              ) : filteredHistory.length === 0 ? (
                <div className="hist-empty">No announcements match the filters.</div>
              ) : (
                <ul className="tab-list">
                  {paged.map((r, i) => {
                    const body = r.Messages || r.Message || "";
                    const shortBody = makeExcerpt(body, 140);
                    return (
                      <li key={`${r.Title}-${r.Date}-${i}`} className="tab-item" onClick={() => openModal(r)}>
                        <div className="tab-row">
                          <span className="notif-dots" />
                          <div className="notif-main">
                            <div className="notif-lefts">
                              <div className="tab-title announcement-title" title={r.Title}>{r.Title}</div>
                              <div className="tab-desc" title={body}>{shortBody}</div>
                            </div>
                          </div>
                        </div>
                        <div className="tab-meta">
                          <span className="badge-dept">{r.Department}</span>
                          <span className="tab-date">
                            {new Date(r.Date || r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      {modalOpen && activeRow &&
        ReactDOM.createPortal(
          <>
            <div className="modal-backdrop" onClick={() => setModalOpen(false)} />
            <div className="ea-modal" role="dialog" aria-modal="true">
            <div className="ea-modal-card">
              <div className="ea-modal-headline">
                <div className="ea-modal-subject">
                  <h3>{activeRow.Title}</h3>
                  <span className="ea-badge-dept-lg">{activeRow.Department || "all"}</span>
                </div>
                <div className="ea-modal-date-lg">
                  {new Date(activeRow.Date || activeRow.date).toLocaleDateString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                  })}
                </div>
              </div>

              <div className="ea-modal-fields">
                <div className="ea-field">
                  <span className="ea-label">Date:</span>
                  <span className="ea-value">
                    {new Date(activeRow.Date || activeRow.date).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })}
                  </span>
                </div>
                <div className="ea-field">
                  <span className="ea-label">Title:</span>
                  <span className="ea-value">{activeRow.Title}</span>
                </div>
                <div className="ea-field">
                  <span className="ea-label">Department:</span>
                  <span className="ea-value">{activeRow.Department || "all"}</span>
                </div>
                <div className="ea-field">
                  <span className="ea-label">Body:</span>
                  <span className="ea-value"><pre>{activeRow.Messages}</pre></span>
                </div>
              </div>

               <div className="modal-actions">
                <button className="btn-danger" onClick={() => handleDelete(activeRow)}>Delete</button>
                <button className="btn-lite" onClick={() => setModalOpen(false)}>Close</button>
              </div>
            </div>
          </div>
          </>,
          document.body
        )
       
        
      }
    </>
  );
}
